import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { getPortfolio, listPortfolios } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Global "selected portfolio" state — the account strip, the desk, the
 * scanner card, the quick-trade dialog and the PLAY button all read from
 * this. Persisted in localStorage so the choice survives reloads.
 *
 * Auth-aware: the list is fetched only once we know the user is
 * authenticated. If a stored portfolio id no longer exists in the
 * returned list we fall back to the first portfolio (never leave a
 * dead id bound).
 */
const PortfolioCtx = createContext(null);

const LS_KEY = "numirock_selected_portfolio_id";

function _pickInitial(list, stored) {
  if (!list?.length) return null;
  if (stored && list.some((p) => p.id === stored)) return stored;
  return list[0].id;
}

export function PortfolioProvider({ children }) {
  const { status } = useAuth();
  const [portfolios, setPortfolios] = useState([]);
  const [selectedId, setSelectedIdRaw] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  // loading == "we are trying to know the list; strip should show a
  // placeholder instead of $0.00 while this is true"
  const [loading, setLoading] = useState(true);

  // Persist writes to localStorage in one place.
  const setSelectedId = useCallback((idOrUpdater) => {
    setSelectedIdRaw((cur) => {
      const next =
        typeof idOrUpdater === "function" ? idOrUpdater(cur) : idOrUpdater;
      try {
        if (next) window.localStorage.setItem(LS_KEY, next);
        else window.localStorage.removeItem(LS_KEY);
      } catch {
        /* ignore private-mode / SSR */
      }
      return next;
    });
  }, []);

  const refreshList = useCallback(async () => {
    try {
      const list = await listPortfolios();
      setPortfolios(list || []);
      setSelectedId((cur) => {
        let stored = cur;
        if (!stored) {
          try {
            stored = window.localStorage.getItem(LS_KEY);
          } catch {
            stored = null;
          }
        }
        return _pickInitial(list, stored);
      });
    } catch {
      // Silent — 401s are handled by the global interceptor which will
      // navigate to /login; other failures leave the previous list in
      // place so a transient hiccup doesn't blow away the strip.
    } finally {
      setLoading(false);
    }
  }, [setSelectedId]);

  // React to auth transitions:
  //   - authed  → fetch the list (works both on cold-load-with-cookie AND
  //               after a form login, which is the case that used to leave
  //               the strip stuck at $0.00).
  //   - guest   → clear everything; localStorage id is left alone so it
  //               can be re-adopted on the next login of the same user.
  //   - loading → keep the loading flag on.
  useEffect(() => {
    if (status === "authed") {
      setLoading(true);
      refreshList();
    } else if (status === "guest") {
      setPortfolios([]);
      setSelectedIdRaw(null);
      setSelectedDetail(null);
      setLoading(false);
    }
  }, [status, refreshList]);

  // Detail fetch — split into (a) id-change clear + fresh fetch and
  // (b) a lightweight 15s poll that refreshes without flashing null.
  const stalePidRef = useRef(null);
  const prevSelIdRef = useRef(null);

  const fetchDetail = useCallback(
    async (pid) => {
      try {
        const d = await getPortfolio(pid);
        setSelectedDetail((prev) =>
          // only accept if the id is still the selected one when the
          // request resolves — prevents an in-flight stale response
          // from overwriting the current view during a fast switch.
          pid === prevSelIdRef.current ? d : prev,
        );
        stalePidRef.current = null;
      } catch (err) {
        // 404 → the id we hold is dead (deleted or scoped to a different
        // user we've now signed out of). Refresh the list — the fresh
        // response drives _pickInitial to first-of-list. Guarded so we
        // don't loop on repeated 404s for the same id.
        if (err?.response?.status === 404 && stalePidRef.current !== pid) {
          stalePidRef.current = pid;
          refreshListRef.current?.();
        }
      }
    },
    [],
  );

  // A ref-to-callback bridge because refreshList is declared LATER than
  // fetchDetail in this file and we don't want a circular useCallback dep.
  const refreshListRef = useRef(null);

  // Keep the refreshList ref in sync so fetchDetail can call it.
  useEffect(() => {
    refreshListRef.current = refreshList;
  }, [refreshList]);

  // (a) Id change → clear old detail immediately, fetch new one.
  useEffect(() => {
    prevSelIdRef.current = selectedId;
    if (!selectedId) {
      setSelectedDetail(null);
      return;
    }
    setSelectedDetail(null);
    fetchDetail(selectedId);
  }, [selectedId, fetchDetail]);

  // (b) 15s poll — refreshes the current selectedDetail without null-flashing.
  useEffect(() => {
    if (!selectedId) return () => {};
    const iv = setInterval(() => {
      fetchDetail(selectedId);
    }, 15000);
    return () => clearInterval(iv);
  }, [selectedId, fetchDetail]);

  const selectPortfolio = useCallback(
    (id) => {
      setSelectedId(id || null);
    },
    [setSelectedId],
  );

  const value = useMemo(
    () => ({
      portfolios,
      selectedId,
      selected: selectedDetail,
      loading,
      selectPortfolio,
      refreshList,
    }),
    [portfolios, selectedId, selectedDetail, loading, selectPortfolio, refreshList],
  );

  return <PortfolioCtx.Provider value={value}>{children}</PortfolioCtx.Provider>;
}

export function usePortfolio() {
  const ctx = useContext(PortfolioCtx);
  if (!ctx) throw new Error("usePortfolio must be used inside PortfolioProvider");
  return ctx;
}
